import React, { useEffect, useRef, useState } from 'react';
import {
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  Input,
  Button,
  Spinner,
} from 'reactstrap';
import axios from 'axios';
import { api } from '../../../config';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import { unwrapApiItems } from '../../../shared/api/unwrap';
import {
  mapSupportDocumentItem,
  mapCounterDocumentItem,
  isOtherSupportDocument,
  mergeDocumentsWithCounters,
} from '../../../shared/offer-domain/document';
import { isOfferDetailReadOnly } from '../utils/offerEditAccess';

const SupportDocumentTab = ({
  ids,
  token,
  formMain = {},
  handleInvitationAccept,
  handleSaveAsDraft,
  fetchDataInvitationValidation,
}) => {
  const [procurementDocuments, setProcurementDocuments] = useState([]);
  const [otherDocuments, setOtherDocuments] = useState([]);
  const [fileCategories, setFileCategories] = useState([]);
  const [uploadModal, setUploadModal] = useState({
    isOpen: false,
    document: null,
  });
  const [customUploadModal, setCustomUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [customFile, setCustomFile] = useState(null);
  const [customDocumentTitle, setCustomDocumentTitle] = useState('');
  const [uploading, setUploading] = useState(false);

  const uploadFileRef = useRef(null);
  const customFileRef = useRef(null);

  const isReadOnly = isOfferDetailReadOnly({
    statusOfferingWordingFlag: formMain.statusOfferingWordingFlag,
    statusOfferingWording: formMain.statusOfferingWording,
    submissionStatus: formMain.submissionStatus,
  });

  const validateAndExecute = async actionFn => {
    let isValid = false;
    try {
      isValid = await fetchDataInvitationValidation(ids, token);
    } catch {
      return;
    }

    if (isValid) {
      toast.warning('Data Already submitted offer');
    } else {
      try {
        await actionFn();
      } catch (error) {
        void error;
      }
    }
  };

  const handleExecuteDraft = () => validateAndExecute(handleSaveAsDraft);
  const handleExecuteOffer = () => validateAndExecute(handleInvitationAccept);

  useEffect(() => {
    if (ids) {
      fetchDocumentUpload(ids);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids]);

  useEffect(() => {
    fetchFileCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFileCategories = async () => {
    try {
      const response = await fetch(
        `${api.API_URL}/document-upload/getall-master-procurement-document`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const result = await response.json();
      const items = result.data.items.map(item => ({
        value: item.id,
        label: item.documentName,
      }));
      setFileCategories(items);
    } catch {
      void 0;
    }
  };

  const unwrapApiDocumentId = payload =>
    payload?.id ??
    payload?.Id ??
    payload?.data?.id ??
    payload?.data?.Id ??
    payload?.procurementDocumentId ??
    payload?.ProcurementDocumentId ??
    null;

  const customCategory = fileCategories.find(cat =>
    /upload custom|custom/i.test(cat.label || '')
  );

  const applySupportDocuments = (items, counters = []) => {
    const normalized = items.map(item => ({
      ...item,
      updated: item.lastUpdated || item.createdDate,
    }));

    const merged = mergeDocumentsWithCounters(normalized, counters);

    setProcurementDocuments(
      merged.filter(item => !isOtherSupportDocument(item, customCategory))
    );
    setOtherDocuments(
      merged.filter(item => isOtherSupportDocument(item, customCategory))
    );
  };

  const fetchDocumentCounters = async procurementId => {
    try {
      const response = await fetch(
        `${api.API_URL}/procurement-document-counter/by-procurement/${procurementId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      return unwrapApiItems(result).map(mapCounterDocumentItem);
    } catch (error) {
      return [];
    }
  };

  const fetchDocumentUpload = async procurementId => {
    try {
      const [docResponse, counters] = await Promise.all([
        fetch(
          `${api.API_URL}/procurement/get-procurement-document?ProcurementId=${procurementId}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        ),
        fetchDocumentCounters(procurementId),
      ]);

      const result = await docResponse.json();
      const items = unwrapApiItems(result)
        .map(mapSupportDocumentItem)
        .filter(item => item.isShowToDMUT === true || item.isShowToDMUT === 1);

      applySupportDocuments(items, counters);
    } catch {
      void 0;
    }
  };

  const handleConfirmDelFileUpload = (id = null, isCustom = false) => {
    if (isReadOnly) return;
    Swal.fire({
      title: 'Are You sure want to Delete this Record?',
      text: 'Deleted Data cannot be Recovered!!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, Delete!',
      cancelButtonText: 'Cancel',
    }).then(result => {
      if (result.isConfirmed) {
        handleDelFileUpload(id, isCustom);
      }
    });
  };

  const handleDelFileUpload = async (id, isCustom = false) => {
    if (!id) {
      toast.error('Document id is not available');
      return;
    }

    try {
      setUploading(true);
      await axios.delete(`${api.API_URL}/procurement-document/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success('Data has been deleted');

      if (isCustom) {
        setOtherDocuments(prev => prev.filter(doc => doc.id !== id));
      } else {
        setProcurementDocuments(prev => prev.filter(doc => doc.id !== id));
      }
    } catch (error) {
      const status = error?.response?.status;
      toast.error(
        status === 404
          ? 'Document not found on server'
          : `Failed to delete data: ${error.message}`
      );
    } finally {
      setUploading(false);
      await fetchDocumentUpload(ids);
    }
  };

  const clearCounterFromState = counterId => {
    setProcurementDocuments(prev =>
      prev.map(doc => {
        if (String(doc.counterId) !== String(counterId)) return doc;
        return {
          ...doc,
          counterId: null,
          hasCounter: false,
          actualUrl: null,
          isMasterDocument: true,
          updated: doc.lastUpdated || doc.createdDate || null,
        };
      })
    );
  };

  const handleConfirmDelCounter = counterId => {
    if (isReadOnly) return;
    if (!counterId) {
      toast.error('Counter document id is not available');
      return;
    }

    Swal.fire({
      title: 'Delete counter document?',
      text: 'Deleted counter data cannot be recovered.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, Delete!',
      cancelButtonText: 'Cancel',
    }).then(result => {
      if (result.isConfirmed) {
        handleDelCounter(counterId);
      }
    });
  };

  const handleDelCounter = async counterId => {
    if (!counterId) {
      toast.error('Counter document id is not available');
      return;
    }

    try {
      setUploading(true);
      const response = await axios.delete(
        `${api.API_URL}/procurement-document-counter/${counterId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const payload = response?.data;
      const isFailed =
        payload?.succeeded === false ||
        payload?.Succeeded === false ||
        payload?.success === false ||
        payload?.Success === false;

      if (isFailed) {
        throw new Error(
          payload?.message || payload?.Message || 'Delete counter failed'
        );
      }

      clearCounterFromState(counterId);
      toast.success('Counter document has been deleted');
      await fetchDocumentUpload(ids);
      // Ensure UI stays cleared if list API is briefly stale
      clearCounterFromState(counterId);
    } catch (error) {
      const status = error?.response?.status;
      toast.error(
        status === 404
          ? 'Counter document not found on server'
          : `Failed to delete counter document: ${error.message}`
      );
      await fetchDocumentUpload(ids);
    } finally {
      setUploading(false);
    }
  };

  const handleDirectOpenNewTab = url => {
    if (!url) {
      toast.error('File is not available');
      return;
    }
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const resetUploadModal = () => {
    setUploadModal({ isOpen: false, document: null });
    setUploadFile(null);
    if (uploadFileRef.current) {
      uploadFileRef.current.value = null;
    }
  };

  const resetCustomModal = () => {
    setCustomUploadModal(false);
    setCustomFile(null);
    setCustomDocumentTitle('');
    if (customFileRef.current) {
      customFileRef.current.value = null;
    }
  };

  const handleCounterDocument = async () => {
    if (isReadOnly) return;
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }

    const doc = uploadModal.document;
    if (!doc) return;

    if (!doc.id) {
      toast.error('Procurement document id is not available');
      return;
    }

    if (!doc.documentUploadId) {
      toast.error('Document category is not available');
      return;
    }

    const isUpdate = Boolean(doc.counterId || doc.hasCounter);

    if (isUpdate && !doc.counterId) {
      toast.error('Counter document id is not available');
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append('ProcurementId', ids);
      formData.append('ProcurementDocumentId', doc.id);
      formData.append('DocumentUploadId', doc.documentUploadId);
      formData.append(
        'FileName',
        doc.documentName || doc.customFileName || uploadFile.name
      );
      formData.append('FileAttachment', uploadFile);
      formData.append('IsShowToDMUT', 'true');

      const endpoint = isUpdate
        ? `${api.API_URL}/procurement-document-counter/${doc.counterId}`
        : `${api.API_URL}/procurement-document-counter`;

      await axios({
        method: isUpdate ? 'put' : 'post',
        url: endpoint,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success(
        isUpdate
          ? 'Counter document has been updated'
          : 'Counter document has been uploaded'
      );
      resetUploadModal();
      await fetchDocumentUpload(ids);
    } catch (error) {
      toast.error(
        `Failed to ${isUpdate ? 'update' : 'upload'} counter document: ${
          error.message
        }`
      );
    } finally {
      setUploading(false);
    }
  };

  const handleCustomUpload = async () => {
    if (isReadOnly) return;
    if (!customDocumentTitle.trim()) {
      toast.error('Please input document title');
      return;
    }
    if (!customFile) {
      toast.error('Please select a file');
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append('ProcurementId', ids);
      formData.append('FileName', customDocumentTitle.trim());
      formData.append('FileAttachment', customFile);
      formData.append('IsShowToDMUT', 'true');

      if (customCategory?.value) {
        formData.append('DocumentUploadId', customCategory.value);
      }

      const response = await axios.post(
        `${api.API_URL}/procurement-document/custom-upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const responseData = response?.data?.data ?? response?.data;
      const newId = unwrapApiDocumentId(responseData);

      if (!newId) {
        // Response may not return id — sync from server list instead of Date.now()
        await fetchDocumentUpload(ids);
        toast.success('File has been uploaded');
        resetCustomModal();
        return;
      }

      const newDoc = {
        id: newId,
        documentName: customDocumentTitle.trim(),
        customFileName: customDocumentTitle.trim(),
        documentUploadId:
          responseData?.documentUploadId ??
          responseData?.DocumentUploadId ??
          customCategory?.value ??
          null,
        actualUrl:
          responseData?.fileUrl ??
          responseData?.FileUrl ??
          responseData?.link ??
          responseData?.Link ??
          null,
        updated: dayjs().format('MMM DD, YYYY'),
      };
      setOtherDocuments(prev => [...prev, newDoc]);

      toast.success('File has been uploaded');
      resetCustomModal();
      await fetchDocumentUpload(ids);
    } catch (error) {
      toast.error(`Failed to upload file: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const renderUploadModal = ({
    isOpen,
    toggle,
    title,
    description,
    selectedFile,
    onFileChange,
    fileInputRef,
    onUpload,
    uploadLabel = 'Upload File',
    uploading,
  }) => (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle} className='border-0 pb-0'>
        <span className='modal-title fw-semibold mb-0'>{title}</span>
      </ModalHeader>
      <ModalBody className='pt-2'>
        <p className='text-muted small mb-3'>
          {description ||
            'Select a file to upload. The existing file will be replaced.'}
        </p>
        <hr className='text-muted opacity-25 mt-0 mb-4' />

        <div className='mb-4'>
          <Label className='form-label'>File</Label>
          <Input
            type='file'
            innerRef={fileInputRef}
            onChange={e => onFileChange(e.target.files?.[0] || null)}
            disabled={uploading}
          />
          {!selectedFile && (
            <small className='text-muted'>No file chosen</small>
          )}
          {selectedFile && (
            <small className='text-muted d-block mt-1'>
              {selectedFile.name}
            </small>
          )}
        </div>

        <div className='d-flex justify-content-end gap-2'>
          <Button color='link' className='text-primary px-3' onClick={toggle}>
            Close
          </Button>
          <Button color='primary' onClick={onUpload} disabled={uploading}>
            {uploading ? (
              <>
                <Spinner size='sm' className='me-2' />
                Uploading...
              </>
            ) : (
              uploadLabel
            )}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );

  return (
    <React.Fragment>
      <Row>
        <Col sm={12}>
          <div className='mb-4'>
            <span className='mb-1 fw-semibold'>Document Attachment</span>
            <p className='text-muted mb-0 fs-12'>
              Bisa jadi Kalimat tambahan untuk membantu section mengenai apa ini
            </p>
          </div>
        </Col>
        <Col sm={12}>
          {!isReadOnly && (
            <div className='mb-4'>
              <div
                role='button'
                tabIndex={0}
                className='border rounded-3 p-3 d-flex align-items-center bg-white'
                style={{
                  cursor: 'pointer',
                  maxWidth: '350px',
                  transition: 'all 0.2s ease-in-out',
                }}
                onClick={() => setCustomUploadModal(true)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setCustomUploadModal(true);
                  }
                }}
              >
                <div
                  className='d-flex align-items-center justify-content-center rounded-circle me-3'
                  style={{
                    width: '48px',
                    height: '48px',
                    backgroundColor: '#f8f9fa',
                    flexShrink: 0,
                  }}
                >
                  <i className='ri-upload-2-line fs-20 text-primary'></i>
                </div>

                <div className='flex-grow-1'>
                  <div className='fs-14 fw-semibold text-dark mb-0'>
                    Upload File
                  </div>
                  <div className='text-muted fs-12'>
                    Browse from your device
                  </div>
                </div>
              </div>
            </div>
          )}
        </Col>

        {/* Procurement Documents Section */}
        <Col sm={12} className='mb-4'>
          <div className='d-flex justify-content-between align-items-center mb-3'>
            <h6 className='fw-semibold text-dark mb-0'>
              Procurement Documents
            </h6>
          </div>

          {procurementDocuments.length === 0 ? (
            <div
              className='border rounded-3 p-4 d-flex align-items-center justify-content-center text-muted bg-light bg-opacity-50'
              style={{ minHeight: '120px' }}
            >
              <i className='ri-file-list-3-line fs-24 me-2' />
              No procurement documents available.
            </div>
          ) : (
            <div>
              {procurementDocuments.map(doc => (
                <div
                  key={doc.id}
                  className='border rounded p-3 mb-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3'
                >
                  <div className='d-flex align-items-center gap-3'>
                    <div
                      className='bg-light rounded d-flex align-items-center justify-content-center'
                      style={{ width: '48px', height: '48px' }}
                    >
                      <i
                        className={`ri-file-text-fill fs-3 ${
                          doc.hasCounter
                            ? 'text-warning'
                            : doc.isMasterDocument
                              ? 'text-success'
                              : 'text-primary'
                        }`}
                      />
                    </div>
                    <div>
                      <h6 className='fw-medium text-dark mb-1'>
                        {doc.documentName || doc.customFileName || '-'}
                      </h6>
                      <div className='d-flex align-items-center gap-2 small'>
                        <span
                          className={`badge ${
                            doc.hasCounter
                              ? 'bg-warning-subtle text-warning'
                              : doc.isMasterDocument
                                ? 'bg-success-subtle text-success'
                                : 'bg-primary-subtle text-primary'
                          }`}
                        >
                          {doc.hasCounter
                            ? 'Countered'
                            : doc.isMasterDocument
                              ? 'Master Document'
                              : 'Upload Custom'}
                        </span>
                        {doc.updated && (
                          <span className='text-muted'>
                            Updated: {doc.updated}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className='d-flex align-items-center gap-2 flex-shrink-0'>
                    <Button
                      color='light'
                      className='border bg-white text-dark rounded-3 fw-medium px-4'
                      onClick={() => handleDirectOpenNewTab(doc.actualUrl)}
                      disabled={!doc.actualUrl}
                    >
                      Download
                    </Button>
                    <Button
                      color='light'
                      className='border bg-white text-dark rounded-3 fw-medium px-4'
                      onClick={() => handleDirectOpenNewTab(doc.actualUrl)}
                      disabled={!doc.actualUrl}
                    >
                      View
                    </Button>
                    {!isReadOnly && (
                      <Button
                        color='light'
                        className='border bg-white text-primary rounded-3 fw-medium px-4'
                        onClick={() =>
                          setUploadModal({ isOpen: true, document: doc })
                        }
                      >
                        {doc.hasCounter ? 'Re-upload' : 'Counter'}
                      </Button>
                    )}
                    {!isReadOnly && doc.hasCounter && (
                      <Button
                        color='light'
                        className='border bg-white text-danger rounded-3 fw-medium px-4'
                        onClick={() => handleConfirmDelCounter(doc.counterId)}
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Col>

        {/* Other Documents Section */}
        <Col sm={12} className='mb-4'>
          <div className='d-flex justify-content-between align-items-center mb-3'>
            <h6 className='fw-semibold text-dark mb-0'>Other Documents</h6>
          </div>

          {otherDocuments.length === 0 ? (
            <div
              className='border rounded-3 p-4 d-flex align-items-center justify-content-center text-muted bg-light bg-opacity-50'
              style={{ minHeight: '120px' }}
            >
              <i className='ri-upload-cloud-2-line fs-24 me-2' />
              The other documents you upload will appear here.
            </div>
          ) : (
            <div>
              {otherDocuments.map(data => (
                <div
                  key={data.id}
                  className='border rounded p-3 mb-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3'
                >
                  <div className='d-flex align-items-center gap-3'>
                    <div
                      className='bg-light rounded d-flex align-items-center justify-content-center'
                      style={{ width: '48px', height: '48px' }}
                    >
                      <i className='ri-file-text-fill text-primary fs-3' />
                    </div>
                    <div>
                      <h6 className='fw-medium text-dark mb-1'>
                        {data.documentName || data.customFileName || '-'}
                      </h6>
                      <div className='d-flex align-items-center gap-2 small'>
                        <span className='badge bg-primary-subtle text-primary'>
                          Upload Custom
                        </span>
                        {data.updated && (
                          <span className='text-muted'>
                            Updated: {data.updated}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className='d-flex align-items-center gap-2 flex-shrink-0'>
                    <Button
                      color='light'
                      className='border bg-white text-dark rounded-3 fw-medium px-4'
                      onClick={() => handleDirectOpenNewTab(data.actualUrl)}
                      disabled={!data.actualUrl}
                    >
                      Download
                    </Button>
                    <Button
                      color='light'
                      className='border bg-white text-dark rounded-3 fw-medium px-4'
                      onClick={() => handleDirectOpenNewTab(data.actualUrl)}
                      disabled={!data.actualUrl}
                    >
                      View
                    </Button>
                    {!isReadOnly && (
                      <Button
                        color='light'
                        className='border bg-white text-danger rounded-3 fw-medium px-4'
                        onClick={() =>
                          handleConfirmDelFileUpload(data.id, true)
                        }
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Col>
      </Row>

      {!isReadOnly && (
        <div className='req-actionbar d-print-none'>
          <Button
            type='button'
            color='success'
            className='d-flex align-items-center gap-2'
            onClick={handleExecuteDraft}
          >
            <i className='bx bx-save'></i>
            <span>Save as Draft</span>
          </Button>
          <Button
            type='button'
            color='primary'
            className='d-flex align-items-center gap-2'
            onClick={handleExecuteOffer}
          >
            <i className='bx bx-send'></i>
            <span>Submit Offer</span>
          </Button>
        </div>
      )}

      {renderUploadModal({
        isOpen: uploadModal.isOpen,
        toggle: resetUploadModal,
        title: (() => {
          const docName =
            uploadModal.document?.documentName ||
            uploadModal.document?.customFileName ||
            'Document';
          if (!uploadModal.document) return 'Upload Document';
          return uploadModal.document.hasCounter
            ? `Re-upload: ${docName}`
            : `Counter: ${docName}`;
        })(),
        description: uploadModal.document?.hasCounter
          ? 'Select a new file to update the existing counter document.'
          : 'Upload a counter document for this procurement document.',
        selectedFile: uploadFile,
        onFileChange: setUploadFile,
        fileInputRef: uploadFileRef,
        onUpload: handleCounterDocument,
        uploadLabel: uploadModal.document?.hasCounter
          ? 'Re-upload File'
          : 'Upload Counter',
        uploading,
      })}

      <Modal isOpen={customUploadModal} toggle={resetCustomModal} centered>
        <ModalHeader toggle={resetCustomModal} className='border-0 pb-0'>
          <span className='modal-title fw-semibold mb-0'>
            Upload Custom Document
          </span>
        </ModalHeader>
        <ModalBody className='pt-2'>
          <p className='text-muted small mb-3'>
            Enter document title and select a file to upload.
          </p>
          <hr className='text-muted opacity-25 mt-0 mb-4' />

          <div className='mb-3'>
            <Label className='form-label'>Document Title</Label>
            <Input
              type='text'
              placeholder='Input document title'
              value={customDocumentTitle}
              onChange={e => setCustomDocumentTitle(e.target.value)}
            />
          </div>

          <div className='mb-4'>
            <Label className='form-label'>File</Label>
            <Input
              type='file'
              innerRef={customFileRef}
              onChange={e => setCustomFile(e.target.files?.[0] || null)}
              disabled={uploading}
            />
            {!customFile && (
              <small className='text-muted'>No file chosen</small>
            )}
            {customFile && (
              <small className='text-muted d-block mt-1'>
                {customFile.name}
              </small>
            )}
          </div>

          <div className='d-flex justify-content-end gap-2'>
            <Button
              color='link'
              className='text-primary px-3'
              onClick={resetCustomModal}
            >
              Close
            </Button>
            <Button
              color='primary'
              onClick={handleCustomUpload}
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <Spinner size='sm' className='me-2' />
                  Uploading...
                </>
              ) : (
                'Upload File'
              )}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default SupportDocumentTab;
